import { useEffect, useState, useRef, useContext } from 'react';
import { getTables, createTable, updateTable, deleteTable, getRestaurantProfile, fetchOrders } from '../utils/api';
import { getRooms, createRoom, updateRoom, deleteRoom } from '../utils/api';
import { QRCodeCanvas } from 'qrcode.react';
import { FaEdit, FaTrash, FaCopy, FaPlus } from 'react-icons/fa';
import AuthContext from '../contexts/AuthContext';
import { db } from '../utils/firebase';
import { collection, onSnapshot } from 'firebase/firestore';

export default function Tables() {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' or 'edit'
  const [tableName, setTableName] = useState('');
  const [editingTableId, setEditingTableId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [toast, setToast] = useState({ show: false, message: '', type: '' });
  const toastTimeout = useRef(null);
  const [tableCapacity, setTableCapacity] = useState(4);
  const { restaurantId } = useContext(AuthContext);
  const [rooms, setRooms] = useState([]);
  const [showRoomModal, setShowRoomModal] = useState(false);
  const [roomModalMode, setRoomModalMode] = useState('add');
  const [roomName, setRoomName] = useState('');
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [roomCapacity, setRoomCapacity] = useState(2);
  const [roomSaving, setRoomSaving] = useState(false);
  const [roomError, setRoomError] = useState('');
  const [roomDeletingId, setRoomDeletingId] = useState(null);
  const [restaurantInfo, setRestaurantInfo] = useState({});
  const [selectedTable, setSelectedTable] = useState(null);
  const [pendingOrders, setPendingOrders] = useState([]);
  const ordersUnsubscribe = useRef(null);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [pendingRoomOrders, setPendingRoomOrders] = useState([]);

  useEffect(() => {
    if (restaurantId) {
      fetchTables();
      fetchRooms();
      fetchRestaurantInfo();
      // Set up real-time listener for orders
      if (ordersUnsubscribe.current) ordersUnsubscribe.current();
      const q = collection(db, `restaurants/${restaurantId}/orders`);
      ordersUnsubscribe.current = onSnapshot(q, (snapshot) => {
        const orders = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPendingOrders(orders.filter(order => order.status === 'pending'));
        setPendingRoomOrders(orders.filter(order => order.status === 'pending' && order.roomId));
      });
    }
    return () => {
      if (ordersUnsubscribe.current) ordersUnsubscribe.current();
    };
    // eslint-disable-next-line
  }, [restaurantId]);

  function isDuplicateTableName(name) {
    return tables.some(t => t.name.trim().toLowerCase() === name.trim().toLowerCase() && t.id !== editingTableId);
  }

  async function fetchTables() {
    setLoading(true);
    setError('');
    try {
      const data = await getTables();
      setTables(data);
    } catch (err) {
      setError('Failed to fetch tables. Are you logged in? Is the backend running?');
    }
    setLoading(false);
  }

  async function fetchRooms() {
    setLoading(true);
    setRoomError('');
    try {
      const data = await getRooms();
      setRooms(data);
    } catch (err) {
      setRoomError('Failed to fetch rooms.');
    }
    setLoading(false);
  }

  async function fetchRestaurantInfo() {
    try {
      const info = await getRestaurantProfile(restaurantId);
      setRestaurantInfo(info);
    } catch (err) {
      setRestaurantInfo({});
    }
  }

  function hasPendingOrder(tableId) {
    return pendingOrders.some(order => order.tableId === tableId && order.status === 'pending');
  }

  function hasPendingRoomOrder(roomId) {
    return pendingRoomOrders.some(order => order.roomId === roomId && order.status === 'pending');
  }

  function openAddModal() {
    setModalMode('add');
    setTableName('');
    setTableCapacity(4);
    setEditingTableId(null);
    setShowModal(true);
  }

  function openEditModal(table) {
    setModalMode('edit');
    setTableName(table.name);
    setTableCapacity(table.capacity || 4);
    setEditingTableId(table.id);
    setShowModal(true);
  }

  function closeModal() {
    setShowModal(false);
    setTableName('');
    setTableCapacity(4);
    setEditingTableId(null);
  }

  function openAddRoomModal() {
    setRoomModalMode('add');
    setRoomName('');
    setRoomCapacity(2);
    setEditingRoomId(null);
    setShowRoomModal(true);
  }

  function openEditRoomModal(room) {
    setRoomModalMode('edit');
    setRoomName(room.name);
    setRoomCapacity(room.capacity || 2);
    setEditingRoomId(room.id);
    setShowRoomModal(true);
  }

  function closeRoomModal() {
    setShowRoomModal(false);
    setRoomName('');
    setRoomCapacity(2);
    setEditingRoomId(null);
  }

  async function handleSaveTable(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    if (isDuplicateTableName(tableName)) {
      setError('Table name must be unique.');
      setSaving(false);
      return;
    }
    try {
      if (modalMode === 'add') {
        await createTable({ name: tableName, capacity: tableCapacity, restaurantId });
        showToast('Table added!');
      } else {
        await updateTable(editingTableId, { name: tableName, capacity: tableCapacity });
        showToast('Table updated!');
      }
      closeModal();
      fetchTables();
    } catch (err) {
      setError('Failed to save table');
      showToast('Failed to save table', 'error');
    }
    setSaving(false);
  }

  async function handleDeleteTable(id) {
    if (!window.confirm('Delete this table?')) return;
    setDeletingId(id);
    setError('');
    try {
      await deleteTable(id);
      fetchTables();
      showToast('Table deleted!');
    } catch (err) {
      setError('Failed to delete table');
      showToast('Failed to delete table', 'error');
    }
    setDeletingId(null);
  }

  async function handleSaveRoom(e) {
    e.preventDefault();
    setRoomSaving(true);
    setRoomError('');
    if (!roomName.trim()) {
      setRoomError('Room name required.');
      setRoomSaving(false);
      return;
    }
    try {
      if (roomModalMode === 'add') {
        await createRoom({ name: roomName, capacity: roomCapacity, restaurantId });
        showToast('Room added!');
      } else {
        await updateRoom(editingRoomId, { name: roomName, capacity: roomCapacity });
        showToast('Room updated!');
      }
      closeRoomModal();
      fetchRooms();
    } catch (err) {
      setRoomError('Failed to save room');
      showToast('Failed to save room', 'error');
    }
    setRoomSaving(false);
  }

  async function handleDeleteRoom(id) {
    if (!window.confirm('Delete this room?')) return;
    setRoomDeletingId(id);
    setRoomError('');
    try {
      await deleteRoom(id);
      fetchRooms();
      showToast('Room deleted!');
    } catch (err) {
      setRoomError('Failed to delete room');
      showToast('Failed to delete room', 'error');
    }
    setRoomDeletingId(null);
  }

  function showToast(message, type = 'success') {
    setToast({ show: true, message, type });
    if (toastTimeout.current) clearTimeout(toastTimeout.current);
    toastTimeout.current = setTimeout(() => setToast({ show: false, message: '', type: '' }), 3000);
  }

  function handleCopy(link) {
    navigator.clipboard.writeText(link);
    showToast('Link copied!');
  }

  return (
    <div className="p-8 bg-rose-50 min-h-screen rounded-2xl">
      <h2 className="text-3xl font-bold mb-6 text-rose-900">Tables Management</h2>
      <div className="flex gap-4 mb-6">
        <button
          className="px-8 py-3 rounded-lg bg-gradient-to-r from-amber-500 to-amber-400 text-white font-bold shadow-lg hover:from-amber-600 hover:to-amber-500 flex items-center gap-3 text-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-amber-400"
          onClick={() => {
            openAddModal();
          }}
        >
          <FaPlus className="text-xl" />
          Add Table
        </button>
        {restaurantInfo.roomFeatureEnabled !== false && (
          <button className="px-6 py-2 rounded bg-gradient-to-r from-rose-500 to-rose-300 text-white font-bold shadow hover:from-rose-600 hover:to-rose-400 flex items-center gap-2" onClick={openAddRoomModal}><FaPlus /> ADD ROOM</button>
        )}
      </div>
      <h3 className="text-xl font-bold mb-2 text-rose-800">Tables</h3>
      {loading ? (
        <div className="text-center py-8">Loading...</div>
      ) : error ? (
        <div className="text-red-500 text-center py-8">{error}</div>
      ) : tables.length === 0 ? (
        <div className="text-gray-500 text-center py-8">No tables found.</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {tables.map(table => {
            return (
              <button
                key={table.id}
                className={`rounded-xl shadow-lg p-6 flex flex-col gap-3 border border-rose-100 hover:shadow-2xl transition text-left focus:outline-none focus:ring-2 focus:ring-amber-400 font-semibold text-lg ${hasPendingOrder(table.id) ? 'bg-red-500 text-white' : 'bg-white text-rose-700'}`}
                onClick={() => setSelectedTable(table)}
                tabIndex={0}
                type="button"
              >
                {table.name} <span className="text-xs text-gray-200">(Capacity: {table.capacity || 4})</span>
                {hasPendingOrder(table.id) && <span className="mt-2 text-xs bg-white text-red-500 rounded-full px-2 py-1 font-bold">Pending Order</span>}
              </button>
            );
          })}
        </div>
      )}
      {/* Only show Rooms section if room feature is enabled */}
      {restaurantInfo.roomFeatureEnabled !== false && (
        <>
          <h3 className="text-xl font-bold mt-10 mb-2 text-rose-800">Rooms</h3>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : roomError ? (
            <div className="text-red-500 text-center py-8">{roomError}</div>
          ) : rooms.length === 0 ? (
            <div className="text-gray-500 text-center py-8">No rooms found.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {rooms.map(room => {
                return (
                  <button
                    key={room.id}
                    className={`rounded-xl shadow-lg p-6 flex flex-col gap-3 border border-rose-100 hover:shadow-2xl transition text-left focus:outline-none focus:ring-2 focus:ring-amber-400 font-semibold text-lg ${hasPendingRoomOrder(room.id) ? 'bg-red-500 text-white' : 'bg-white text-rose-700'}`}
                    onClick={() => setSelectedRoom(room)}
                    tabIndex={0}
                    type="button"
                  >
                    {room.name} <span className="text-xs text-gray-200">(Capacity: {room.capacity || 2})</span>
                    {hasPendingRoomOrder(room.id) && <span className="mt-2 text-xs bg-white text-red-500 rounded-full px-2 py-1 font-bold">Pending Order</span>}
                  </button>
                );
              })}
            </div>
          )}
          {/* Room Details Modal */}
          {selectedRoom && (
            <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) setSelectedRoom(null); }}>
              <div className="bg-white rounded-lg p-6 w-full max-w-sm relative shadow-xl">
                <button className="absolute top-2 right-2 text-gray-400" onClick={() => setSelectedRoom(null)}>&times;</button>
                <h3 className="text-lg font-bold mb-4">{selectedRoom.name} (Capacity: {selectedRoom.capacity || 2})</h3>
                <div className="flex flex-col items-center gap-2 mb-4">
                  <div className="text-xs text-gray-500">QR Code:</div>
                  <QRCodeCanvas value={`${window.location.origin}/room/${restaurantId}/${selectedRoom.id}`} size={120} />
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs break-all text-gray-600">{`${window.location.origin}/room/${restaurantId}/${selectedRoom.id}`}</span>
                    <button className="ml-2 text-rose-700 hover:text-rose-900" onClick={() => handleCopy(`${window.location.origin}/room/${restaurantId}/${selectedRoom.id}`)} title="Copy link"><FaCopy /></button>
                  </div>
                </div>
                <div className="flex gap-2 justify-end mt-4">
                  <button className="text-rose-700 hover:text-rose-900" onClick={() => { openEditRoomModal(selectedRoom); setSelectedRoom(null); }} title="Edit"><FaEdit /></button>
                  <button className="text-rose-700 hover:text-rose-900" onClick={() => { handleDeleteRoom(selectedRoom.id); setSelectedRoom(null); }} title="Delete"><FaTrash /></button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm relative shadow-xl">
            <button className="absolute top-2 right-2 text-gray-400" onClick={closeModal}>&times;</button>
            <h3 className="text-lg font-bold mb-4">{modalMode === 'add' ? 'Add Table' : 'Edit Table'}</h3>
            <form onSubmit={handleSaveTable} className="space-y-3">
              <div>
                <label className="block text-sm font-semibold mb-1">Table Name/Number</label>
                <input type="text" className="border px-3 py-2 rounded w-full" value={tableName} onChange={e => setTableName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Capacity</label>
                <input type="number" min={1} max={20} className="border px-3 py-2 rounded w-full" value={tableCapacity} onChange={e => setTableCapacity(Number(e.target.value))} required />
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="btn btn-primary w-full" disabled={saving}>{modalMode === 'add' ? 'Add' : 'Update'}</button>
                <button type="button" className="btn btn-outline w-full" onClick={closeModal}>Cancel</button>
              </div>
              {error && <div className="text-red-500 text-xs mt-2">{error}</div>}
            </form>
          </div>
        </div>
      )}
      {showRoomModal && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-sm relative shadow-xl">
            <button className="absolute top-2 right-2 text-gray-400" onClick={closeRoomModal}>&times;</button>
            <h3 className="text-lg font-bold mb-4">{roomModalMode === 'add' ? 'Add Room' : 'Edit Room'}</h3>
            <form onSubmit={handleSaveRoom} className="space-y-3">
              <div>
                <label className="block text-sm font-semibold mb-1">Room Number/Name</label>
                <input type="text" className="border px-3 py-2 rounded w-full" value={roomName} onChange={e => setRoomName(e.target.value)} required />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-1">Capacity</label>
                <input type="number" min={1} max={20} className="border px-3 py-2 rounded w-full" value={roomCapacity} onChange={e => setRoomCapacity(Number(e.target.value))} required />
              </div>
              <div className="flex gap-2 mt-4">
                <button type="submit" className="btn btn-primary w-full" disabled={roomSaving}>{roomModalMode === 'add' ? 'Add' : 'Update'}</button>
                <button type="button" className="btn btn-outline w-full" onClick={closeRoomModal}>Cancel</button>
              </div>
              {roomError && <div className="text-red-500 text-xs mt-2">{roomError}</div>}
            </form>
          </div>
        </div>
      )}
      {/* Table Details Modal */}
      {selectedTable && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50" onClick={e => { if (e.target === e.currentTarget) setSelectedTable(null); }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-sm relative shadow-xl">
            <button className="absolute top-2 right-2 text-gray-400" onClick={() => setSelectedTable(null)}>&times;</button>
            <h3 className="text-lg font-bold mb-4">{selectedTable.name} (Capacity: {selectedTable.capacity || 4})</h3>
            <div className="flex flex-col items-center gap-2 mb-4">
              <div className="text-xs text-gray-500">QR Code:</div>
              <QRCodeCanvas value={`${window.location.origin}/table/${restaurantId}/${selectedTable.id}`} size={120} />
              <div className="flex items-center gap-2 mt-1">
                <span className="text-xs break-all text-gray-600">{`${window.location.origin}/table/${restaurantId}/${selectedTable.id}`}</span>
                <button className="ml-2 text-rose-700 hover:text-rose-900" onClick={() => handleCopy(`${window.location.origin}/table/${restaurantId}/${selectedTable.id}`)} title="Copy link"><FaCopy /></button>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-4">
              <button className="text-rose-700 hover:text-rose-900" onClick={() => { openEditModal(selectedTable); setSelectedTable(null); }} title="Edit"><FaEdit /></button>
              <button className="text-rose-700 hover:text-rose-900" onClick={() => { handleDeleteTable(selectedTable.id); setSelectedTable(null); }} title="Delete"><FaTrash /></button>
            </div>
          </div>
        </div>
      )}
      {toast.show && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-2 rounded shadow-lg text-white ${toast.type === 'error' ? 'bg-red-500' : 'bg-green-600'}`}>{toast.message}</div>
      )}
    </div>
  );
} 